import React, { useState } from 'react';

import { IPersonaFront } from '@api/routers/user/service';
import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';

import {
  IStakedDeposit,
  usePersonaOwnerMatches,
} from '@web/features/staking/hooks';
import { Avatar, Checkbox, Tooltip } from '@web/ui/components';
import { BcBaseIcon, BcEthIcon } from '@web/ui/icons';

import { GridItemMenu } from './GridItemMenu';
import { StakedGridItemDetails } from './StakedGridItemDetails';
import { UnstakedGridItemDetails } from './UnstakedGridItemDetails';

const reflect = keyframes`
  0% { transform: translateX(-64px) }
  100% { transform: translateX(calc(500%)) }
`;

const StakingPersonaContainer = styled.div<{
  isProcessing: boolean;
  isMenuOpen: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: 100%;
  border-radius: ${({ theme }) => theme.shape.corner['0500']};
  box-sizing: border-box;

  .avatar {
    img {
      transition: all 150ms ease;
    }
  }

  .menu-button {
    visibility: ${({ isMenuOpen }) => (isMenuOpen ? 'visible' : 'hidden')};
  }

  .hover-show {
    display: none;
  }

  :hover {
    .checkbox,
    .menu-button {
      visibility: visible;
    }

    .hover-hide {
      display: none;
    }

    .hover-show {
      display: flex;
    }

    .avatar {
      img {
        transform: ${({ isProcessing }) => !isProcessing && 'scale(1.03)'};
      }
    }
  }

  ${({ theme }) => theme.breakpoints.down('lg')} {
    .hover-show {
      display: flex;
    }

    :hover {
      .hover-hide {
        display: flex;
      }
    }
  }
`;

const CheckboxContainer = styled.div<{ selected: boolean }>`
  visibility: ${({ selected }) => (selected ? 'visible' : 'hidden')};
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  cursor: pointer;
  top: ${({ theme }) => theme.spacing('0500')};
  left: ${({ theme }) => theme.spacing('0500')};
  width: ${({ theme }) => theme.size['2000']};
  height: ${({ theme }) => theme.size['2000']};
  border-radius: ${({ theme }) => theme.shape.corner['0500']};
  background: ${({ theme }) => theme.color.surfaceContainer};
  z-index: 1;

  :active {
    border-radius: ${({ theme }) => theme.shape.corner['0750']};
  }
`;

const AvatarContainer = styled.div<{ isProcessing: boolean }>`
  width: 100%;
  position: relative;
  pointer-events: ${({ isProcessing }) => (isProcessing ? 'none' : 'auto')};
  overflow: hidden;
  background: ${({ theme, isProcessing }) =>
    isProcessing && isProcessing ? theme.color.background : 'none'};
  opacity: ${({ theme, isProcessing }) =>
    isProcessing ? theme.opacity.solid : theme.opacity.opaque};

  ${({ isProcessing }) =>
    isProcessing &&
    css`
      ::after {
        position: absolute;
        top: 0;
        bottom: 0;
        content: '';
        display: block;
        width: 20%;
        background: linear-gradient(
          to right,
          rgba(255, 255, 255, 0),
          rgba(255, 255, 255, 1)
        );
        animation: ${reflect} 2s ease-out infinite;
      }
    `}
`;

const StyledAvatar = styled(Avatar)<{ locked: boolean }>`
  width: 100%;
  opacity: ${({ locked }) => (locked ? 0.5 : 1)};
  border-radius: ${({ theme }) => theme.shape.corner['1000']};
`;

const BlockchainIcon = styled.span`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing('1000')};
  right: ${({ theme }) => theme.spacing('0750')};
  z-index: 2;
`;

export const DetailsContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing('1000')};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: ${({ theme }) => theme.spacing('1000')};
  padding-inline: ${({ theme }) => theme.spacing('0500')};
`;

export interface IStakingGridItemProps {
  selected: boolean;
  persona: IPersonaFront;
  deposit: IStakedDeposit | null;
  locked?: boolean;
  onClick(): void;
}

export const StakingGridItem = ({
  persona,
  deposit,
  selected,
  locked = false,
  onClick,
}: IStakingGridItemProps) => {
  const t = useTranslations();
  const isSynced = !!(usePersonaOwnerMatches(persona) || deposit);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleMenuOpening = (isMenuOpen: boolean) => setIsMenuOpen(isMenuOpen);

  return (
    <StakingPersonaContainer isProcessing={!isSynced} isMenuOpen={isMenuOpen}>
      {isSynced && (
        <CheckboxContainer selected={selected} className="checkbox">
          <Checkbox checked={selected} onChange={onClick} disabled={locked} />
        </CheckboxContainer>
      )}
      <GridItemMenu
        persona={persona}
        deposit={deposit}
        className="menu-button"
        disabled={!isSynced}
        onMenuOpen={handleMenuOpening}
      />
      <AvatarContainer isProcessing={!isSynced}>
        <StyledAvatar
          className="avatar"
          tokenId={persona.tokenId}
          borderRadius="1000"
          locked={locked}
        />
        <Tooltip
          content={`${persona?.blockchain} ${t('STAKING_GRID_ITEM_NETWORK')}`}
          placement="top"
        >
          <BlockchainIcon>
            {persona?.blockchain === 'base' ? <BcBaseIcon /> : <BcEthIcon />}
          </BlockchainIcon>
        </Tooltip>
      </AvatarContainer>
      <DetailsContainer>
        {deposit ? (
          <StakedGridItemDetails
            persona={persona}
            deposit={deposit}
            disabled={!isSynced}
          />
        ) : (
          <UnstakedGridItemDetails
            persona={persona}
            processing={!isSynced}
            locked={locked}
          />
        )}
      </DetailsContainer>
    </StakingPersonaContainer>
  );
};
